
import Vue from 'vue';
import '@/assets/css/helpdesk/helpdesk-theme.css';
import Component from 'vue-class-component';
import {Watch} from 'vue-property-decorator';
import {namespace} from 'vuex-class';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import EmailHtmlFrame from '@/components/helpdesk/EmailHtmlFrame.vue';
import RichTextEditor from '@/components/helpdesk/RichTextEditor.vue';
import SignatureSelector from '@/components/helpdesk/SignatureSelector.vue';
import HelpdeskLinkedUsersTab from '@/components/helpdesk/HelpdeskLinkedUsersTab.vue';
import HelpdeskAssignee from '@/components/helpdesk/HelpdeskAssignee.vue';
import HelpdeskAssigneePicker from '@/components/helpdesk/HelpdeskAssigneePicker.vue';
import HelpdeskRelatedEmailsTab from '@/components/helpdesk/HelpdeskRelatedEmailsTab.vue';
import {inboxEmailRoute, ticketRelatedEmails} from '@/helpers/helpdeskTicketEmails';
import {ticketStatusLabel, ticketStatusOptions, ticketStatusShortLabel} from '@/helpers/helpdeskTicketStatus';
import {IHelpdeskTicket} from '@/store/modules/helpdeskTicketsModule';
import {ITicketType} from '@/store/modules/helpdeskTicketTypesModule';
import {IEmailTemplate} from '@/store/modules/helpdeskEmailTemplatesModule';

const mod = namespace('helpdeskTicketsModule');

@Component({name: 'HelpdeskTickets', components: {HelpdeskAssignee, HelpdeskAssigneePicker, EmailHtmlFrame, PaginationBar, RichTextEditor, SignatureSelector, HelpdeskLinkedUsersTab, HelpdeskRelatedEmailsTab}})
export default class HelpdeskTickets extends Vue {
  @mod.Getter('tickets')     private tickets!: IHelpdeskTicket[];
  @mod.Getter('currentPage') private currentPage!: number;
  @mod.Getter('lastPage')      private lastPage!: number;
  @mod.Getter('total')         private total!: number;
  @mod.Getter('perPage')       private perPage!: number;

  private dialog              = false;
  private detailDialog        = false;
  // Ticket whose detail opened the edit dialog: closing that dialog (save or cancel) returns to it.
  private returnToDetailId: number | null = null;
  private savingAssigneeId: number | null = null;
  private detailTab           = 0;
  private detailLinkedCount   = 0;
  private inboxEmailRoute     = inboxEmailRoute;
  private templateReplyDialog = false;
  private confirmDelete       = false;
  private saving              = false;
  private sendingTemplate     = false;
  private valid               = false;
  private loadingTypes        = false;
  private loadingTemplates    = false;
  private editTarget: IHelpdeskTicket | null = null;
  private detailTicket: IHelpdeskTicket | null = null;
  private deleteTarget: IHelpdeskTicket | null = null;
  private selectedPerPage     = 15;
  private filterEmailId: number | null = null;
  private subscribes: any     = {};
  private search              = '';
  private filterStatuses: string[] = [];
  private filterTypeId: number | null = null;
  private filterUnread: boolean | null = null;
  private sortBy: 'created_at' | 'last_email_received_at' = 'created_at';
  private filtersExpanded     = false;
  private searchTimer: any    = null;
  private templateReplyId: number | null = null;
  private templateSignatureBody = '';

  private form = {
    subject:             '',
    status:              'open',
    ticket_type_id:      null as number | null,
    reporter_email:      '',
    assignee_email:      '',
    description:         '',
    autoReplyTemplateId: null as number | null,
  };

  get statusOptions() {
    return ticketStatusOptions(this.$i18n);
  }

  private headers = [
    {text: 'ID',         value: 'id',             width: 60},
    {text: 'Asunto',     value: 'subject',        width: 300},
    {text: 'Tipo',       value: 'ticket_type',    sortable: false, width: 200},
    {text: 'Estado',     value: 'status',         width: 100},
    {text: 'Informador', value: 'reporter_email', sortable: false, width: 160},
    {text: 'Creado',     value: 'created_at',     width: 130},
    {text: '',           value: 'actions',        sortable: false, width: 100},
  ];

  get ticketTypes(): ITicketType[] {
    return this.$store.getters['helpdeskTicketTypesModule/types'];
  }

  get allTemplates(): IEmailTemplate[] {
    return this.$store.getters['helpdeskEmailTemplatesModule/allTemplates'];
  }

  get selectedTemplateName(): string {
    if (!this.form.autoReplyTemplateId) return '';
    const t = this.allTemplates.find(x => x.id === this.form.autoReplyTemplateId);
    return t ? t.name : '';
  }

  get templateReplyPreview(): string {
    if (!this.templateReplyId) return '';
    const t = this.allTemplates.find(x => x.id === this.templateReplyId);
    return t ? t.body : '';
  }

  get detailRelatedEmails(): string[] {
    return ticketRelatedEmails(this.detailTicket);
  }

  get reporterEmail(): string {
    const userData = this.$store.getters['authModule/userData'];
    return (userData && userData.email) ? userData.email : '';
  }

  get hasActiveFilters(): boolean {
    return this.filterStatuses.length > 0 || !!this.filterTypeId || !!this.filterUnread;
  }

  get activeFilterCount(): number {
    return (this.filterStatuses.length > 0 ? 1 : 0) + (this.filterTypeId ? 1 : 0) + (this.filterUnread ? 1 : 0);
  }

  created() {
    const emailId = this.$route.query.email_id;
    if (emailId) {
      this.filterEmailId = parseInt(emailId as string);
    }
    this.subscribes = (this as any).$ons({
      'backend.call.OK.ticketsLoaded':    () => {},
      'backend.call.OK.ticketLoaded':     () => {
        if (this.detailDialog) {
          this.detailTicket = this.$store.getters['helpdeskTicketsModule/currentTicket'];
          // Opening the ticket reads its new replies; the detail still marks them as new.
          if (this.detailTicket && this.detailTicket.unread_emails_count) {
            this.$store.dispatch('helpdeskTicketsModule/markTicketEmailsAsRead', this.detailTicket.id);
          }
        }
      },
      'backend.call.OK.ticketCreated':    (payload: any) => {
        this.saving = false;
        this.dialog = false;
        this.loadData();
        // Auto-reply: once ticket is created, send template if selected
        if (this.pendingTemplateId && payload && payload.id) {
          this.dispatchTemplateReply(payload.id, this.pendingTemplateId, undefined, this.reporterEmail);
        }
        this.pendingTemplateId = null;
      },
      'backend.call.KO.ticketCreated':    () => { this.saving = false; this.pendingTemplateId = null; },
      'backend.call.OK.ticketUpdated':    (payload: any) => {
        this.saving = false;
        this.savingAssigneeId = null;
        this.dialog = false;
        // The detail modal keeps its own copy: refresh it, keeping relations the PATCH response omits.
        if (this.detailTicket && payload && payload.id === this.detailTicket.id) {
          this.detailTicket = {...this.detailTicket, ...payload, linked_users: payload.linked_users || this.detailTicket.linked_users};
        }
      },
      'backend.call.KO.ticketUpdated':    () => { this.saving = false; this.savingAssigneeId = null; },
      'backend.call.OK.ticketDeleted':    () => { this.loadData(); },
      'backend.call.OK.templateReplySent': () => {
        this.sendingTemplate     = false;
        this.templateReplyDialog = false;
        this.templateReplyId     = null;
        // Refresh detail — ticketLoaded event will update detailTicket
        if (this.detailTicket) {
          this.$store.dispatch('helpdeskTicketsModule/fetchTicket', this.detailTicket.id);
        }
      },
      'backend.call.KO.templateReplySent': () => { this.sendingTemplate = false; },
    });
    this.loadData();

    const openId = this.$route.query.open_ticket;
    if (openId) {
      this.openDetailById(parseInt(openId as string, 10));
    }
  }

  beforeDestroy() { (this as any).$offs(this.subscribes); }

  // Holds the template ID to send after ticket creation
  private pendingTemplateId: number | null = null;

  private buildParams(page?: number): any {
    const params: any = {page: page || this.currentPage, per_page: this.selectedPerPage};
    if (this.filterEmailId)         params.email_id       = this.filterEmailId;
    if (this.search)                params.search         = this.search;
    if (this.filterStatuses.length) params.statuses       = this.filterStatuses;
    if (this.filterTypeId)          params.ticket_type_id = this.filterTypeId;
    if (this.filterUnread)          params.has_unread     = true;
    if (this.sortBy !== 'created_at') params.sort         = this.sortBy;
    return params;
  }

  loadData() {
    this.$store.dispatch('helpdeskTicketsModule/fetchTickets', this.buildParams());
  }

  private onPageChange(page: number) {
    this.$store.dispatch('helpdeskTicketsModule/fetchTickets', this.buildParams(page));
  }

  private onSearchInput() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadData(), 400);
  }

  private onSearchClear() {
    this.search = '';
    this.loadData();
  }

  private onFilterChange() { this.loadData(); }

  private onPerPageChange(value: number) {
    this.selectedPerPage = value;
    this.loadData();
  }

  private clearEmailFilter() {
    this.filterEmailId = null;
    this.$router.replace({name: 'helpdeskTickets'});
    this.loadData();
  }

  private loadTypes() {
    this.loadingTypes = true;
    this.$store.dispatch('helpdeskTicketTypesModule/fetchTypes', {all: true})
      .then(() => { this.loadingTypes = false; })
      .catch(() => { this.loadingTypes = false; });
  }

  private loadAllTemplates() {
    this.loadingTemplates = true;
    this.$store.dispatch('helpdeskEmailTemplatesModule/fetchAllTemplates')
      .then(() => { this.loadingTemplates = false; })
      .catch(() => { this.loadingTemplates = false; });
  }

  private openCreate() {
    this.editTarget = null;
    this.form = {
      subject: '', status: 'open', ticket_type_id: null,
      reporter_email: this.reporterEmail, assignee_email: '',
      description: '', autoReplyTemplateId: null,
    };
    this.saving = false;
    this.loadTypes();
    this.loadAllTemplates();
    this.dialog = true;
  }

  private openEdit(item: IHelpdeskTicket) {
    this.editTarget = item;
    this.form = {
      subject:             item.subject,
      status:              item.status,
      ticket_type_id:      item.ticket_type_id,
      reporter_email:      item.reporter_email || '',
      assignee_email:      item.assignee_email || '',
      description:         item.description || '',
      autoReplyTemplateId: null,
    };
    this.saving = false;
    this.loadTypes();
    this.loadAllTemplates();
    this.dialog = true;
  }

  // Assignee picked in the grid or the detail header: saved immediately, nothing else changes.
  private changeAssignee(ticket: IHelpdeskTicket, email: string) {
    if (this.savingAssigneeId !== null) return;
    this.savingAssigneeId = ticket.id;
    this.$store.dispatch('helpdeskTicketsModule/updateTicket', {id: ticket.id, data: {assignee_email: email || null}});
  }

  private editFromDetail() {
    if (!this.detailTicket) return;
    this.returnToDetailId = this.detailTicket.id;
    this.detailDialog = false;
    this.openEdit(this.detailTicket);
  }

  @Watch('dialog')
  private onEditDialogToggled(open: boolean) {
    if (open || this.returnToDetailId === null) return;
    const id = this.returnToDetailId;
    this.returnToDetailId = null;
    // Re-fetched, so the detail shows what was just saved.
    this.openDetailById(id);
  }

  private openDetail(item: IHelpdeskTicket) {
    this.detailTicket = item;
    this.detailTab    = 0;
    this.detailLinkedCount = 0;
    this.detailDialog = true;
    this.loadAllTemplates();
    this.$store.dispatch('helpdeskTicketsModule/fetchTicket', item.id);
  }

  private openDetailById(id: number) {
    this.detailTicket = null;
    this.detailTab    = 0;
    this.detailLinkedCount = 0;
    this.detailDialog = true;
    this.loadAllTemplates();
    this.$store.dispatch('helpdeskTicketsModule/fetchTicket', id);
  }

  private openTemplateReplyFromDetail() {
    this.templateReplyId = null;
    this.templateSignatureBody = '';
    this.templateReplyDialog = true;
  }

  private doSendTemplateReply() {
    if (!this.templateReplyId || !this.detailTicket) return;
    this.sendingTemplate = true;
    this.dispatchTemplateReply(this.detailTicket.id, this.templateReplyId, this.templateSignatureBody || undefined, this.reporterEmail);
  }

  private dispatchTemplateReply(ticketId: number, templateId: number, signatureBody?: string, sentBy?: string) {
    this.$store.dispatch('helpdeskTicketsModule/sendTemplateReply', {ticketId, templateId, signatureBody, sentBy});
  }

  private onSave() {
    const formRef = this.$refs.form as any;
    if (!formRef.validate()) return;
    this.saving = true;
    const data: any = {
      subject:        this.form.subject,
      status:         this.form.status,
      ticket_type_id: this.form.ticket_type_id || undefined,
      reporter_email: this.form.reporter_email || undefined,
      assignee_email: this.form.assignee_email || null,
      description:    this.form.description || undefined,
    };
    if (this.editTarget) {
      // On edit: if template selected, send right away after update
      if (this.form.autoReplyTemplateId) {
        this.pendingTemplateId = this.form.autoReplyTemplateId;
        const unsub = (this as any).$ons({
          'backend.call.OK.ticketUpdated': () => {
            if (this.pendingTemplateId) {
              this.dispatchTemplateReply(this.editTarget!.id, this.pendingTemplateId, undefined, this.reporterEmail);
              this.pendingTemplateId = null;
            }
            (this as any).$offs(unsub);
          },
          'backend.call.KO.ticketUpdated': () => {
            this.pendingTemplateId = null;
            (this as any).$offs(unsub);
          },
        });
      }
      this.$store.dispatch('helpdeskTicketsModule/updateTicket', {id: this.editTarget.id, data});
    } else {
      // On create: pendingTemplateId is read in the ticketCreated handler
      this.pendingTemplateId = this.form.autoReplyTemplateId;
      this.$store.dispatch('helpdeskTicketsModule/createTicket', data);
    }
  }

  private onDelete(item: IHelpdeskTicket) {
    this.deleteTarget = item;
    this.confirmDelete = true;
  }

  private doDelete() {
    if (!this.deleteTarget) return;
    this.confirmDelete = false;
    this.$store.dispatch('helpdeskTicketsModule/deleteTicket', this.deleteTarget.id);
  }

  private statusLabel(status: string): string {
    return ticketStatusLabel(this.$i18n, status);
  }

  private statusShortLabel(status: string): string {
    return ticketStatusShortLabel(this.$i18n, status);
  }


  private statusBarColor(status: string): string {
    const map: {[k: string]: string} = {open: '#1976d2', pending: '#ff9800', resolved: '#4caf50', closed: '#9e9e9e'};
    return map[status] || '#9e9e9e';
  }

  private formatDate(date: string): string {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: '2-digit'}) +
      ' ' + d.toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'});
  }

  private stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120);
  }
}
